import { useMemo, useState, type ReactNode } from 'react';
import { Box, Button, HStack, Input, Stack, Text } from '@chakra-ui/react';
import {
    STYLE_SHAPE_RATIOS,
    applyStylePreset,
    givenStylePreset,
    hasOwnLook,
    isBuiltInStyleName,
    nearestStyleShape,
    partsRemovedBy,
    styleOfTile,
    stylePresetsFor,
    type Dashboard,
    type Grid,
    type ScreenTile,
    type StylePreset,
    type StyleShape,
} from '@casa/model';
import { CanvasMagnificationContext, GroupTileView, TileEnvironmentContext, TileView, previewTileOf, sampleEnvironment, useTileEnvironment } from '@casa/tiles';
import { ChoiceField, ScreenRow } from '../forms/form_rows.tsx';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { tileRect, type GridGeometry } from '../screens/grid.ts';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeNote } from '../ui/chrome_page.tsx';
import { ConfirmDialog } from '../ui/confirm_dialog.tsx';
import { useElementSize } from '../ui/use_element_size.ts';
import { newTileOf, type NewTileStyle } from './dashboard_edits.ts';
import { catalogEntry, defaultSpan, spanForShape, type CatalogType } from './tile_catalog.ts';

// Styles to pick (tile-styles.md): a grid of the styles for the tile's type (TY-1), each drawn as
// the tile with that style, at its size on the screen, with its live data or sample data (TY-5).
// It is used two ways (TY-6): the row "Style" in a tile's dialog, where picking over a look of the
// tile's own asks first (TY-3) and a style made for another shape says so (TY-10); and the step
// "Style" when adding a tile, each style at its own shape, which gives the new tile its size
// (TY-10). A type no style is for has neither. The user's own styles are saved from a tile ("Save
// as style…") and renamed or deleted under the grid (TY-9).

type Preset = StylePreset & { readonly id?: string };

interface Size {
    width: number;
    height: number;
}

/** A style in a grid, drawn as `tile` at `size`; `mark` says something more under its name. */
interface GridItem {
    readonly key: string;
    readonly name: string;
    readonly tile: ScreenTile;
    readonly size: Size;
    readonly mark?: string | undefined;
}

const DASHBOARDS = { key: 'dashboard', name: "The dashboard's" } as const;

/** The shapes of a preview's area, width over height: from a little taller than square to wide. */
const PREVIEW_RATIO = { min: 0.75, max: 2 } as const;

const keyOf = (preset: Preset) : string => preset.id ?? `own:${preset.name}`;

/** Whether a style styles the caption: then its preview shows a sample one (TY-5). */
const stylesCaption = (preset: Preset | undefined) : boolean => ['captionColor', 'captionSize', 'captionAlign'].some((key) => preset?.style[key as keyof Preset['style']] !== undefined);

/** `tile` with `preset` applied, as its preview shows it (TY-5); the type's name as the title where it has none. */
function previewOf(tile: ScreenTile, preset: Preset | undefined, title: string) : ScreenTile {
    if (tile.type === 'group') {
        return { ...tile, label: tile.label ?? title };
    }

    return previewTileOf(applyStylePreset(tile, preset), title, stylesCaption(preset));
}

/** What a style would throw away of `tile`: its own look, or a part it takes away (TY-2, TY-3); nothing to ask about otherwise. */
function lossOf(tile: ScreenTile, preset: Preset | undefined, isOwn: boolean) : string | undefined {
    const removed = partsRemovedBy(tile, preset);
    if (removed.length > 0) {
        return `This takes away the tile's ${removed.join(' and ')}${removed.includes('gauge') ? ', with its range and thresholds' : ''}${isOwn ? ', and replaces its own style and look' : ''}. Go on?`;
    }

    return isOwn ? "This replaces the tile's own style and look. Go on?" : undefined;
}

/** The styles to pick from for a tile of `type`: the built-in ones, then the dashboard's own (TY-9). */
export const presetsOf = (type: ScreenTile['type'], dashboard: Dashboard) : Preset[] => stylePresetsFor(type, dashboard.tileStyles);

/** A span's size on the screen; before the screen is laid out, about as a cell of the 4-column grid, 80 pixels. */
function sizeOn(geometry: GridGeometry | undefined, span: Size) : Size {
    return geometry ? tileRect({ x: 1, y: 1, ...span }, geometry) : { width: 80 * span.width, height: 80 * span.height };
}

/** The span of a new tile of `type` for a style made for `shape`: its usual one for any shape (TY-10). */
function spanFor(type: CatalogType, shape: StyleShape | undefined, grid: Grid, geometry: GridGeometry | undefined) : Size {
    const span = defaultSpan(catalogEntry(type), grid);

    return shape === undefined ? span : spanForShape(span, STYLE_SHAPE_RATIOS[shape], grid, (each) => sizeOn(geometry, each));
}

/** A preview is drawn at most this many times its real size. */
const MAX_PREVIEW_SCALE = 3;

/**
 * The tile laid out at `size`, its real size on the screen, so that its title, value and icon keep
 * their proportions, and scaled to fit an area `ratio` times as wide as high, centred. Scaled up,
 * its canvases draw with as many more pixels, so that it stays sharp.
 */
function StylePreview({ tile, size, ratio, shrink = 1 }: { tile: ScreenTile; size: Size; ratio: number; shrink?: number }) : ReactNode {
    const [ref, box] = useElementSize<HTMLDivElement>();
    const scale = box.width > 0 ? Math.min(MAX_PREVIEW_SCALE, box.width / size.width, box.height / size.height) * shrink : 0;

    return (
        <Box ref={ref} width="100%" aspectRatio={ratio} position="relative" pointerEvents="none">
            {scale > 0 ? (
                <div
                    style={{
                        position: 'absolute',
                        top: (box.height - size.height * scale) / 2,
                        left: (box.width - size.width * scale) / 2,
                        width: size.width,
                        height: size.height,
                        transform: `scale(${scale})`,
                        transformOrigin: '0 0',
                    }}
                >
                    <CanvasMagnificationContext.Provider value={Math.max(1, scale)}>
                        {tile.type === 'group' ? <GroupTileView group={tile} /> : <TileView tile={tile} />}
                    </CanvasMagnificationContext.Provider>
                </div>
            ) : null}
        </Box>
    );
}

/** A preview fills at most this much more of its cell than the smallest one in the grid, where it is not wider than the cell: the same (the owner, 2026-10-29: "shrink it just a little more"). */
const MOST_AREA = 1;

/**
 * How much smaller each preview is drawn than fitting its cell would make it (the owner,
 * 2026-10-29: square ones looked much larger than wide ones beside them). A preview not wider than
 * the cell, which fills its height, is made smaller to fill at most the area of the smallest preview
 * in the grid (`MOST_AREA`); a wider one, which fills its width, is left. Previews of one
 * shape are all left.
 */
function shrinksOf(items: readonly GridItem[], ratio: number) : Map<string, number> {
    // A preview's share of the cell's area, fitted: a wider one by its width, another by its height.
    const areaOf = (item: GridItem) : number => {
        const own = item.size.width / item.size.height;

        return own >= ratio ? ratio / own : own / ratio;
    };
    const smallest = Math.min(...items.map(areaOf));

    return new Map(items.map((item) => {
        const own = item.size.width / item.size.height;

        return [item.key, own > ratio ? 1 : Math.min(1, Math.sqrt((MOST_AREA * smallest) / areaOf(item)))];
    }));
}

/**
 * The grid of styles: two to a row, one on a phone (the owner, 2026-10-29), every cell the same
 * (with three, a thin border scaled down could not be seen). Each preview is fitted into an area of
 * one shape, the most common one among them (the middle one), from 3:4 to 2:1: an existing tile's
 * previews, all of its shape, fill it. The names line up. Its tiles show sample data for what they
 * have no source for.
 */
function StyleGrid({ items, current, onPick }: { items: readonly GridItem[]; current: string | undefined; onPick: (key: string) => void }) : ReactNode {
    const live = useTileEnvironment();
    const environment = useMemo(() => sampleEnvironment(live), [live]);
    const hasMarks = items.some((item) => item.mark !== undefined);
    const ratios = items.map((item) => item.size.width / item.size.height).sort((a, b) => a - b);
    const ratio = Math.min(PREVIEW_RATIO.max, Math.max(PREVIEW_RATIO.min, ratios[Math.floor(ratios.length / 2)] ?? 1));
    const shrinks = shrinksOf(items, ratio);

    return (
        <TileEnvironmentContext.Provider value={environment}>
            <Box display="grid" gridTemplateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }} gap="3">
                {items.map((item) => {
                    const isCurrent = item.key === current;

                    return (
                        <Box
                            as="button"
                            key={item.key}
                            aria-label={item.name}
                            aria-pressed={isCurrent}
                            data-style-choice={item.key}
                            onClick={() => onPick(item.key)}
                            display="flex"
                            flexDirection="column"
                            textAlign="center"
                            p="1.5"
                            borderRadius="lg"
                            borderWidth="2px"
                            borderColor={isCurrent ? 'blue.400' : 'transparent'}
                            _hover={{ bg: 'bg.muted' }}
                            cursor="pointer"
                        >
                            <StylePreview tile={item.tile} size={item.size} ratio={ratio} shrink={shrinks.get(item.key) ?? 1} />
                            <Text fontSize="sm" mt="1" fontWeight={isCurrent ? 'semibold' : 'normal'}>
                                {item.name}
                            </Text>
                            {/* Its room kept in every cell where any has one, so that the names line up. */}
                            {hasMarks ? (
                                <Text fontSize="xs" color="fg.muted">
                                    {item.mark ?? ' '}
                                </Text>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
        </TileEnvironmentContext.Provider>
    );
}

/** A question before a style replaces a look of its own (TY-3). */
function ReplaceQuestion({ name, message, onReplace, onKeep }: { name: string; message: string; onReplace: () => void; onKeep: () => void }) : ReactNode {
    return <ConfirmDialog title={`Use "${name}"?`} message={message} confirm="Replace" cancel="Keep its own" onConfirm={onReplace} onCancel={onKeep} />;
}

export interface StylePickerRowProps {
    tile: ScreenTile;
    dashboard: Dashboard;
    /** The tile's size on the screen, in pixels; unknown before the screen is laid out. */
    tileSize: Size | undefined;
    /** The title a preview shows for a tile without one: its type's name. */
    title: string;
    onChange: (tile: ScreenTile) => void;
    /** One of the dashboard's own styles saved, in place of the one named `previous` where there is one (a rename). */
    onSaveStyle: (style: StylePreset, previous?: string) => void;
    /** One of the dashboard's own styles for the tile's type deleted. */
    onDeleteStyle: (name: string) => void;
}

/** What is wrong with `name` for a style of the user's own for `type`; `others` are the names it may not take. */
function nameProblem(type: ScreenTile['type'], name: string, others: readonly string[]) : string | undefined {
    const trimmed = name.trim();
    if (trimmed === '') {
        return 'A name is needed.';
    }
    if (isBuiltInStyleName(type, trimmed)) {
        return `"${trimmed}" is a built-in style.`;
    }

    return others.includes(trimmed) ? `You have a style "${trimmed}" already.` : undefined;
}

/** A new name for one of the user's own styles. */
function RenameDialog({ type, name, others, onRename, onClose }: { type: ScreenTile['type']; name: string; others: readonly string[]; onRename: (name: string) => void; onClose: () => void }) : ReactNode {
    const [typed, setTyped] = useState(name);
    const problem = typed.trim() === name ? undefined : nameProblem(type, typed, others);

    return (
        <ChromeDialog
            title={`Rename "${name}"`}
            onClose={onClose}
            footer={
                <>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button colorPalette="blue" disabled={problem !== undefined} onClick={() => onRename(typed.trim())}>
                        Rename
                    </Button>
                </>
            }
        >
            <Stack gap="3">
                <Input aria-label="Name" value={typed} onChange={(event) => setTyped(event.target.value)} />
                {problem ? <ChromeNote status="warning" text={problem} /> : null}
                <Text fontSize="sm" color="fg.muted">
                    Tiles given this style show its new name.
                </Text>
            </Stack>
        </ChromeDialog>
    );
}

/** The user's own styles for the tile's type, under the grid: renamed or deleted (TY-9). */
function OwnStyles({ type, own, onSaveStyle, onDeleteStyle }: { type: ScreenTile['type']; own: readonly StylePreset[]; onSaveStyle: StylePickerRowProps['onSaveStyle']; onDeleteStyle: StylePickerRowProps['onDeleteStyle'] }) : ReactNode {
    const [renaming, setRenaming] = useState<StylePreset>();
    const [deleting, setDeleting] = useState<StylePreset>();
    if (own.length === 0) {
        return null;
    }

    return (
        <>
            <SettingsGroup title="Your styles">
                {own.map((style) => (
                    <SettingsRow
                        key={style.name}
                        title={style.name}
                        detail={style.shape ? `For ${style.shape} tiles` : 'For any shape'}
                        opens={false}
                        end={
                            <HStack gap="2">
                                <Button size="xs" variant="outline" onClick={() => setRenaming(style)}>
                                    Rename
                                </Button>
                                <Button size="xs" variant="outline" colorPalette="red" onClick={() => setDeleting(style)}>
                                    Delete
                                </Button>
                            </HStack>
                        }
                    />
                ))}
            </SettingsGroup>
            {renaming ? (
                <RenameDialog
                    type={type}
                    name={renaming.name}
                    others={own.map((style) => style.name).filter((name) => name !== renaming.name)}
                    onRename={(name) => {
                        onSaveStyle({ ...renaming, name }, renaming.name);
                        setRenaming(undefined);
                    }}
                    onClose={() => setRenaming(undefined)}
                />
            ) : null}
            {deleting ? (
                <ConfirmDialog
                    title={`Delete "${deleting.name}"?`}
                    message="Tiles given this style keep how they look."
                    confirm="Delete"
                    cancel="Keep it"
                    onConfirm={() => {
                        onDeleteStyle(deleting.name);
                        setDeleting(undefined);
                    }}
                    onCancel={() => setDeleting(undefined)}
                />
            ) : null}
        </>
    );
}

const SHAPE_CHOICES = [
    { value: 'any', label: 'Any shape' },
    { value: 'wide', label: 'Wide' },
    { value: 'square', label: 'Square' },
    { value: 'tall', label: 'Tall' },
] as const;

export interface SaveStyleRowProps {
    tile: ScreenTile;
    dashboard: Dashboard;
    /** The tile's size on the screen, for the shape it suggests; unknown before the screen is laid out. */
    tileSize: Size | undefined;
    onChange: (tile: ScreenTile) => void;
    onSaveStyle: StylePickerRowProps['onSaveStyle'];
}

/**
 * "Save as style…" in a tile's dialog (TY-9): a name and a shape, the one nearest the tile's first
 * (TY-10); the tile's style and look options are saved as one of the dashboard's own styles for its
 * type, and the tile is given it. A name of one of the user's own replaces that style.
 */
export function SaveStyleRow({ tile, dashboard, tileSize, onChange, onSaveStyle }: SaveStyleRowProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const size = tileSize ?? sizeOn(undefined, tile.layout);
    const [name, setName] = useState('');
    const [shape, setShape] = useState<string>(nearestStyleShape(size.width / size.height));
    if (tile.type === 'group') {
        return null;
    }

    const own = (dashboard.tileStyles ?? []).filter((style) => style.type === tile.type).map((style) => style.name);
    const trimmed = name.trim();
    const problem = nameProblem(tile.type, name, []);
    const isReplacing = own.includes(trimmed);
    const save = () : void => {
        onSaveStyle(styleOfTile(tile, trimmed, shape === 'any' ? undefined : (shape as StyleShape)));
        onChange({ ...tile, styleName: trimmed });
        setName('');
        setOpen(false);
    };

    return (
        <ScreenRow
            label="Save as style…"
            value=""
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="4">
                    <Text fontSize="sm" color="fg.muted">
                        Keeps this tile&apos;s style and how it looks as a style of your own, kept with the dashboard, to pick for other {catalogEntry(tile.type).name.toLowerCase()} tiles.
                    </Text>
                    <SettingsGroup>
                        <Box px="4" py="3">
                            <Input aria-label="Style name" placeholder="A name, such as Living room" value={name} onChange={(event) => setName(event.target.value)} />
                        </Box>
                        <ChoiceField label="Made for" options={SHAPE_CHOICES} value={shape} onChange={(picked) => setShape(picked ?? 'any')} helper="The shape of tile the style is made for; a new tile given it takes that shape." />
                    </SettingsGroup>
                    {trimmed !== '' && problem ? <ChromeNote status="warning" text={problem} /> : null}
                    {isReplacing ? <ChromeNote status="info" text={`This replaces your style "${trimmed}". Tiles given it keep how they look.`} /> : null}
                    <Button colorPalette="blue" alignSelf="flex-end" disabled={problem !== undefined} onClick={save}>
                        {isReplacing ? 'Replace' : 'Save'}
                    </Button>
                </Stack>
            }
        />
    );
}

/** The row "Style" in a tile's dialog: each style at the tile's own size (TY-10); none for a type no style is for. */
export function StylePickerRow({ tile, dashboard, tileSize, title, onChange, onSaveStyle, onDeleteStyle }: StylePickerRowProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const [asking, setAsking] = useState<{ key: string; message: string }>();

    const presets = presetsOf(tile.type, dashboard);
    const given = givenStylePreset(tile, presets);
    const isOwn = !given && hasOwnLook(tile);
    const current = given ? keyOf(given) : isOwn ? undefined : DASHBOARDS.key;
    const size = tileSize ?? sizeOn(undefined, tile.layout);
    const shape = nearestStyleShape(size.width / size.height);
    const choices: { key: string; name: string; preset: Preset | undefined }[] = [
        { ...DASHBOARDS, preset: undefined },
        ...presets.map((preset) => ({ key: keyOf(preset), name: preset.name, preset })),
    ];
    const items: GridItem[] = choices.map((choice) => ({
        key: choice.key,
        name: choice.name,
        tile: previewOf(tile, choice.preset, title),
        size,
        mark: choice.preset?.shape !== undefined && choice.preset.shape !== shape ? `Made for ${choice.preset.shape} tiles` : undefined,
    }));

    const apply = (key: string) : void => {
        onChange(applyStylePreset(tile, choices.find((choice) => choice.key === key)?.preset));
        setAsking(undefined);
        setOpen(false);
    };
    const pick = (key: string) : void => {
        if (key === current) {
            setOpen(false);

            return;
        }

        const message = lossOf(tile, choices.find((choice) => choice.key === key)?.preset, isOwn);
        if (message) {
            setAsking({ key, message });
        } else {
            apply(key);
        }
    };

    if (presets.length === 0) {
        return null;
    }

    return (
        <>
            <ScreenRow
                label="Style"
                value={given ? given.name : isOwn ? 'Its own' : DASHBOARDS.name}
                isOpen={isOpen}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                screen={
                    <Stack gap="3">
                        <Text fontSize="sm" color="fg.muted">
                            A style sets the tile style and how the {title.toLowerCase()} tile looks; what it shows stays. Its style rules stay too.
                        </Text>
                        <StyleGrid items={items} current={current} onPick={pick} />
                        <OwnStyles type={tile.type} own={(dashboard.tileStyles ?? []).filter((style) => style.type === tile.type)} onSaveStyle={onSaveStyle} onDeleteStyle={onDeleteStyle} />
                    </Stack>
                }
            />
            {asking ? (
                <ReplaceQuestion
                    name={choices.find((choice) => choice.key === asking.key)?.name ?? ''}
                    message={asking.message}
                    onReplace={() => apply(asking.key)}
                    onKeep={() => setAsking(undefined)}
                />
            ) : null}
        </>
    );
}

export interface NewTileStyleDialogProps {
    type: Exclude<CatalogType, 'group'>;
    dashboard: Dashboard;
    geometry: GridGeometry | undefined;
    onPick: (style: NewTileStyle) => void;
    onClose: () => void;
}

/**
 * The step "Style" when adding a tile (TY-6), after its type, where styles are for it: "The dashboard's" first and chosen,
 * each style at its own shape, one for any shape at the type's usual size (TY-10). A tap on one
 * adds the tile with that style, at that size.
 */
export function NewTileStyleDialog({ type, dashboard, geometry, onPick, onClose }: NewTileStyleDialogProps) : ReactNode {
    const { name } = catalogEntry(type);
    const choices = [{ ...DASHBOARDS, preset: undefined as Preset | undefined }, ...presetsOf(type, dashboard).map((preset) => ({ key: keyOf(preset), name: preset.name, preset }))].map((choice) => {
        const span = spanFor(type, choice.preset?.shape, dashboard.grid, geometry);
        const tile = newTileOf(type, span);

        return { ...choice, span, tile: previewOf(tile, choice.preset, name) };
    });
    const items: GridItem[] = choices.map((choice) => ({ key: choice.key, name: choice.name, tile: choice.tile, size: sizeOn(geometry, choice.span) }));

    const pick = (key: string) : void => {
        const choice = choices.find((each) => each.key === key);
        if (choice) {
            onPick({ preset: choice.preset, span: choice.span });
        }
    };

    return (
        <ChromeDialog title={`New ${name.toLowerCase()} tile: its style`} onClose={onClose} focusesField={false} fillsPhone>
            <Stack gap="3">
                <Text fontSize="sm" color="fg.muted">
                    How the tile looks, and its shape. Its data is chosen next; the style can be changed later in its dialog.
                </Text>
                <StyleGrid items={items} current={DASHBOARDS.key} onPick={pick} />
            </Stack>
        </ChromeDialog>
    );
}
